// packages/agent-kit/src/conversations/useConversationDetail.ts
// --- FULL AND UPDATED ---

import useSWR from 'swr';
import { useMemo } from 'react';
import { conversationService, isApiError } from '@dopomogai/api-client';
import type { ConversationDetail, UUID, Message } from '@dopomogai/types';
import { AgentKitError } from '../types';
import type { UIMessage } from '../types';

interface ConversationDetailOptions {
  includeInternal?: boolean;
}

function safeParseArgs(args: any): Record<string, any> {
  try {
    if (typeof args === 'string') return JSON.parse(args);
    if (args && typeof args === 'object') return args;
  } catch {}
  return args || {};
}

function safeParseOutput(output: any): any {
  if (typeof output !== 'string') return output;
  try {
    return JSON.parse(output);
  } catch {
    return output;
  }
}

const processBackendMessagesToUIMessages = (
  rawMessages: Message[] | undefined,
  includeInternal: boolean,
): UIMessage[] => {
  if (!rawMessages) return [];

  const uiMessages: UIMessage[] = [];

  rawMessages.forEach((msg, msgIndex) => {
    const baseKey = (msg as any).id || `${msg.timestamp || ''}-${msgIndex}`;

    msg.parts?.forEach((part, partIndex) => {
      const id = `${baseKey}-${part.part_kind}-${partIndex}`;

      switch (part.part_kind) {
        case 'user-prompt':
          if (part.content && typeof part.content === 'string') {
            uiMessages.push({ type: 'user', id, content: part.content, timestamp: msg.timestamp });
          }
          break;

        case 'llm-response':
        case 'text':
          if (part.content && typeof part.content === 'string') {
            uiMessages.push({
              type: 'assistant',
              id,
              content: part.content,
              model: msg.model_name || undefined,
              timestamp: msg.timestamp,
              isStreaming: false,
            });
          }
          break;

        case 'tool-call': {
          const toolCallId = part.tool_call_id || id;
          uiMessages.push({
            type: 'tool_call_start',
            id: toolCallId,
            tool_call_id: toolCallId,
            tool_name: part.tool_name || 'unknown_tool',
            arguments: safeParseArgs((part as any).args),
            timestamp: msg.timestamp,
          });
          break;
        }

        // Accept all shapes
        case 'tool-response':
        case 'tool-result':
        case 'tool-return': {
          const toolResponseId = `resp-for-${part.tool_call_id || id}`;
          uiMessages.push({
            type: 'tool_response_end',
            id: toolResponseId,
            tool_call_id: part.tool_call_id || '',
            tool_name: part.tool_name || 'unknown_tool',
            output: safeParseOutput((part as any).output ?? (part as any).content ?? ''),
            timestamp: msg.timestamp,
          });
          break;
        }

        // Show system prompt if present only when internals are enabled.
        case 'system-instruction': {
          if (!includeInternal) break;
          const raw = (part as any).content;
          const contentStr = typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2);
          uiMessages.push({
            type: 'system_log',
            id,
            content: contentStr,
            severity: 'info',
            timestamp: msg.timestamp,
          } as any);
          break;
        }

        case 'system-prompt': {
          if (!includeInternal) break;
          const raw = (part as any).content;
          const contentStr = typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2);
          uiMessages.push({
            type: 'system_log',
            id,
            content: contentStr,
            severity: 'info',
            timestamp: msg.timestamp,
          } as any);
          break;
        }

        case 'error':
          // Not rendered by default in detail page unless desired
          break;
      }
    });
  });

  return uiMessages;
};

const conversationDetailFetcher = async (
  [, conversationId, includeInternal]: [string, UUID, boolean]
): Promise<ConversationDetail> => {
  const response = await conversationService.getConversationById(conversationId, Boolean(includeInternal));
  if (isApiError(response)) throw new AgentKitError(response.message, response);
  return response;
};

export const useConversationDetail = (
  conversationId: UUID | null,
  options: ConversationDetailOptions = {}
) => {
  const { includeInternal = false } = options;
  // IMPORTANT: key carries the actual boolean so SWR re-fetches correctly
  const cacheKey = conversationId ? ['conversation', conversationId, includeInternal] : null;

  const { data: conversation, error, isLoading, mutate } =
    useSWR<ConversationDetail, AgentKitError>(cacheKey, conversationDetailFetcher);

  const processedMessages = useMemo(
    () => processBackendMessagesToUIMessages(conversation?.messages, includeInternal),
    [conversation, includeInternal]
  );

  return { conversation, processedMessages, error, isLoading, mutate };
};
